import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ParticleBackground from '../components/ParticleBackground';
import { authService } from '../services/api/authService';
import { storage } from '../utils/storage';
import { cn } from '../lib/utils';
import './MagicLinkPage.scss';

// Import ShadCN components
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

interface MagicLinkPageProps {
  onLoginSuccess: (userType: 'patient') => void;
}

export function MagicLinkPage({ onLoginSuccess }: MagicLinkPageProps) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [magicLinkData, setMagicLinkData] = useState<{
    session_id: number;
    patient_id: number;
    phone_no?: string;
  } | null>(null);

  // Use useRef to prevent double execution in React.StrictMode
  const hasInitiatedVerification = useRef(false);

  // Generate storage key based on session ID to make it session-specific
  const getStorageKey = () => `magic_link_verification_${sessionId}`;

  // Check for stored verification data
  const getStoredVerificationData = () => {
    try {
      const stored = sessionStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading stored verification data:', error);
      return null;
    }
  };

  // Store verification data
  const storeVerificationData = (data: any) => {
    try {
      sessionStorage.setItem(getStorageKey(), JSON.stringify(data));
    } catch (error) {
      console.error('Error storing verification data:', error);
    }
  };

  // Clear stored verification data
  const clearStoredVerificationData = () => {
    try {
      sessionStorage.removeItem(getStorageKey());
    } catch (error) {
      console.error('Error clearing stored verification data:', error);
    }
  };

  useEffect(() => {
    console.log('🔍 MagicLinkPage useEffect triggered - checking for stored verification or verifying magic link');
    
    // First check if we have stored verification data
    const storedData = getStoredVerificationData();
    if (storedData) {
      console.log('✅ Found stored verification data, using it instead of API call');
      setMagicLinkData(storedData);
      setIsVerifying(false);
      return;
    }

    // Prevent double execution in React.StrictMode during development
    if (!hasInitiatedVerification.current) {
      hasInitiatedVerification.current = true;
      console.log('✅ No stored data found, proceeding with API verification');
      verifyMagicLinkToken();
    } else {
      console.log('⚠️ Duplicate execution prevented (React.StrictMode)');
    }
  }, []);

  const verifyMagicLinkToken = async () => {
    console.log('🔄 Starting magic link verification...');
    
    const token = searchParams.get('token');
    
    if (!token) {
      setError('Invalid magic link - missing token');
      setIsVerifying(false);
      return;
    }

    if (!sessionId) {
      setError('Invalid magic link - missing session ID');
      setIsVerifying(false);
      return;
    }

    try {
      setIsVerifying(true);
      setError('');
      
      const response = await authService.verifyMagicLink(token);
      
      if (response.status === 'success') {
        console.log('✅ Magic link verification complete');

        // Check if this is an external session with direct auth (skip OTP)
        if (response.data.is_external && response.data.auth) {
          const specialty = searchParams.get('specialty');
          storage.setMagicLinkAuthData(
            response.data.auth,
            response.data,
            specialty || undefined
          );
          clearStoredVerificationData();
          onLoginSuccess('patient');
          navigate('/patient/dashboard', { replace: true });
          return;
        }

        // Regular session: show OTP form
        // Verify session ID matches URL parameter
        if (response.data.session_id.toString() !== sessionId) {
          setError('Session ID mismatch - invalid magic link');
          return;
        }

        // Store the verification data for future page refreshes
        storeVerificationData(response.data);
        setMagicLinkData(response.data);
      } else {
        setError(response.message || 'Magic link verification failed');
      }
    } catch (err) {
      console.error('Magic link verification error:', err);
      setError('Failed to verify magic link. Please check if the link is valid and not expired.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmitOTP = async () => {
    if (!magicLinkData || otp.length !== 6) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const authResponse = await authService.validateMagicLinkOTP(magicLinkData.phone_no!, otp, magicLinkData.patient_id);

      // Extract specialty from URL query parameters
      const specialty = searchParams.get('specialty');

      // Store enhanced authentication data with specialty if present
      storage.setMagicLinkAuthData(authResponse, magicLinkData, specialty || undefined);

      // Clear the stored verification data since we're now authenticated
      clearStoredVerificationData();

      // Success - notify parent component
      onLoginSuccess('patient');

      // Navigate to consultation for this specific session
      navigate(`/patient/dashboard`, { replace: true });

    } catch (err) {
      const error = err as Error;
      console.error('Magic link OTP validation error:', error);
      setError('Invalid OTP. Please check the code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp.length === 6 && !isLoading) {
      handleSubmitOTP();
    }
  };

  // Clear stored data when navigating away to info page
  const handleGoToInfo = () => {
    clearStoredVerificationData();
    navigate('/patient/info');
  };

  if (isVerifying) {
    return (
      <div className="flex justify-center items-center min-h-screen relative bg-transparent" data-component="MagicLinkPage">
        <ParticleBackground />
        <div className="absolute inset-0 flex items-center justify-center">
          <Card className="relative z-10 w-full max-w-[92%] xs:max-w-[85%] sm:max-w-md bg-white/95 backdrop-blur-md rounded-[20px] shadow-lg transform -translate-y-5 animate-slideUp px-3 xs:px-4 sm:px-6 py-5 xs:py-6 sm:py-8">
            <CardContent className="flex flex-col items-center justify-center p-8 text-center">
              <div className="animate-pulse mb-4">
                <img
                  src="/aigp-logo.png"
                  alt="AIGP Health"
                  className="h-16 xs:h-20 sm:h-24 w-auto"
                />
              </div>
              <p className="text-lg font-medium text-[#2c3e50] mb-2">Verifying Magic Link</p>
              <span className="text-sm text-[#6c757d]">Please wait...</span>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error && !magicLinkData) {
    return (
      <div className="flex justify-center items-center min-h-screen relative bg-transparent" data-component="MagicLinkPage">
        <ParticleBackground />
        <div className="absolute inset-0 flex items-center justify-center">
          <Card className="relative z-10 w-full max-w-[92%] xs:max-w-[85%] sm:max-w-md bg-white/95 backdrop-blur-md rounded-[20px] shadow-lg transform -translate-y-5 animate-slideUp px-3 xs:px-4 sm:px-6 py-5 xs:py-6 sm:py-8">
            <CardHeader className="space-y-2 text-center p-0 pb-3 xs:pb-4">
              <div className="flex justify-center mb-4">
                <img
                  src="/aigp-logo.png"
                  alt="AIGP Health"
                  className="h-16 xs:h-20 sm:h-24 w-auto"
                />
              </div>
              <h2 className="text-[1.15rem] xs:text-lg sm:text-[1.5rem] font-semibold text-[#dc3545]">
                Invalid Magic Link
              </h2>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-[#dc3545] bg-[#fff5f5]">
                <AlertDescription className="text-[#dc3545]">
                  {error}
                </AlertDescription>
              </Alert>
              <Button
                onClick={handleGoToInfo}
                className="w-full h-11 xs:h-12 sm:h-14 rounded-[12px] bg-gradient-to-r from-[#356BAD] to-[#356BAD] text-white font-semibold"
              >
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen relative bg-transparent" data-component="MagicLinkPage">
      <ParticleBackground />

      <div className="absolute inset-0 flex items-center justify-center">
        <Card className="relative z-10 w-full max-w-[92%] xs:max-w-[85%] sm:max-w-md bg-white/95 backdrop-blur-md rounded-[20px] shadow-lg transform -translate-y-5 animate-slideUp px-3 xs:px-4 sm:px-6 py-5 xs:py-6 sm:py-8">
          <CardHeader className="space-y-2 text-center p-0 pb-3 xs:pb-4">
            <div className="flex justify-center">
              <img
                src="/aigp-logo.png"
                alt="AIGP Health"
                className="h-16 xs:h-20 sm:h-24 w-auto mb-1 xs:mb-2 sm:mb-4 transition-transform hover:scale-[1.02]"
              />
            </div>
            <h2 className="text-[1.15rem] xs:text-lg sm:text-[1.5rem] font-semibold bg-gradient-to-r from-[#1E355C] to-[#356BAD] bg-clip-text text-transparent leading-tight tracking-[-0.3px] px-1 xs:px-2">
              Enter Verification Code
            </h2>
            <p className="text-[0.85rem] xs:text-sm sm:text-[0.95rem] text-[#64748b] mt-1.5 xs:mt-2 mb-4 xs:mb-5 sm:mb-6 px-0.5 xs:px-1 leading-relaxed font-normal">
              Please enter the 6-digit code sent to {magicLinkData?.phone_no}
            </p>
          </CardHeader>

          <CardContent className="space-y-5 xs:space-y-6 sm:space-y-7 p-0">
            {error && (
              <Alert className="border-[#dc3545] bg-[#fff5f5]">
                <AlertDescription className="text-[#dc3545] text-center text-[0.85rem] xs:text-sm sm:text-base font-medium">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="otp" className="text-[0.9rem] xs:text-sm sm:text-base font-medium text-[#2c3e50]">
                Verification Code
              </Label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={handleOtpChange}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                className={cn(
                  "h-11 xs:h-12 sm:h-14 rounded-[12px] text-center text-lg xs:text-xl sm:text-2xl font-mono tracking-widest border-2 transition-all",
                  "focus:border-[#356BAD] focus:ring-2 focus:ring-[#356BAD]/20",
                  error ? "border-[#dc3545]" : "border-[#e2e8f0]"
                )}
                maxLength={6}
                disabled={isLoading}
              />

            </div>

            <Button
              onClick={handleSubmitOTP}
              disabled={otp.length !== 6 || isLoading}
              className={cn(
                "w-full h-11 xs:h-12 sm:h-14 rounded-[12px] bg-gradient-to-r from-[#356BAD] to-[#356BAD] text-white font-semibold shadow-[0_4px_6px_rgba(125,184,224,0.15)] transition-all",
                "hover:translate-y-[-1px] hover:shadow-[0_6px_8px_rgba(125,184,224,0.2)]",
                "active:translate-y-0",
                "disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              )}
            >
              {isLoading ? 'Verifying...' : 'Start Consultation'}
            </Button>

            <div className="text-center">
              <Button
                variant="ghost"
                onClick={handleGoToInfo}
                className="text-[0.85rem] xs:text-sm text-[#6c757d] hover:text-[#2c3e50] hover:bg-transparent"
                disabled={isLoading}
              >
                Need Help? Get Info
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 